import axios from "axios";
import { resolveApiUrl } from "../assets/GlobalVariables";
import { belvedereContract } from "../data/belvedereContractData";

const PUBLIC_BASE = resolveApiUrl("/api/campaigns/room-inventory");

export const BELVEDERE_INVENTORY_CAMPAIGN_CODE =
  belvedereContract?.hotelCode || "royal-belvedere-2026";

export const BELVEDERE_HOLD_TTL_MINUTES = 15;

/** Poll interval while search results are visible (agent/admin). */
export const BELVEDERE_AVAILABILITY_POLL_MS = 60_000;

function buildRoomsFromOffer(offer) {
  const breakdown = Array.isArray(offer?.roomBreakdown) ? offer.roomBreakdown : [];
  if (breakdown.length > 0) {
    const merged = new Map();
    for (const row of breakdown) {
      const code = Number(row?.room?.code ?? row?.roomCode ?? row?.room_code);
      if (!Number.isFinite(code) || code <= 0) continue;
      merged.set(code, (merged.get(code) || 0) + 1);
    }
    return [...merged.entries()].map(([room_code, qty]) => ({ room_code, qty }));
  }
  const code = Number(offer?.room?.code ?? offer?.roomCode ?? offer?.code);
  if (!Number.isFinite(code) || code <= 0) return [];
  return [{ room_code: code, qty: 1 }];
}

export function buildBelvedereHoldRoomsFromSelectedRoom(selectedRoom) {
  if (!selectedRoom) return [];
  if (Array.isArray(selectedRoom.roomBreakdown) && selectedRoom.roomBreakdown.length) {
    return buildRoomsFromOffer(selectedRoom);
  }
  if (Array.isArray(selectedRoom.rooms) && selectedRoom.rooms.length) {
    const merged = new Map();
    for (const row of selectedRoom.rooms) {
      const code = Number(row?.code ?? row?.roomCode ?? row?.room_code ?? row?.room?.code);
      if (!Number.isFinite(code) || code <= 0) continue;
      merged.set(code, (merged.get(code) || 0) + 1);
    }
    return [...merged.entries()].map(([room_code, qty]) => ({ room_code, qty }));
  }
  if (Array.isArray(selectedRoom.componentCodes) && selectedRoom.componentCodes.length) {
    const merged = new Map();
    for (const raw of selectedRoom.componentCodes) {
      const code = Number(raw);
      if (!Number.isFinite(code) || code <= 0) continue;
      merged.set(code, (merged.get(code) || 0) + 1);
    }
    return [...merged.entries()].map(([room_code, qty]) => ({ room_code, qty }));
  }
  const code = Number(
    selectedRoom?.room?.code ?? selectedRoom?.roomCode ?? selectedRoom?.code,
  );
  if (!Number.isFinite(code) || code <= 0) return [];
  const qty = Math.max(1, Number(selectedRoom.quantity) || 1);
  return [{ room_code: code, qty }];
}

export async function fetchBelvedereCampaignAvailability({
  checkIn,
  checkOut,
  roomCodes,
  campaignCode = BELVEDERE_INVENTORY_CAMPAIGN_CODE,
} = {}) {
  const params = new URLSearchParams();
  params.set("campaign_code", campaignCode);
  params.set("check_in", checkIn);
  params.set("check_out", checkOut);
  if (Array.isArray(roomCodes) && roomCodes.length) {
    params.set("room_codes", roomCodes.join(","));
  }
  const res = await axios.get(`${PUBLIC_BASE}/availability?${params.toString()}`, {
    withCredentials: true,
  });
  return res.data;
}

/**
 * Enrich room offers with inventory snapshot.
 * remaining for a multi-room combo = min remaining across required room codes,
 * after accounting for qty needed of each code within the combo.
 *
 * Campaign Stop Sale (any night in stay) arrives as room.status === "sold_out"
 * and/or room.stop_sale === true from GET /availability — both block bookability.
 */
export function enrichOffersWithInventory(offers, availabilityPayload) {
  const byCode = new Map();
  for (const row of availabilityPayload?.rooms || []) {
    byCode.set(Number(row.room_code), row);
  }

  return (offers || []).map((offer) => {
    const needed = buildRoomsFromOffer(offer);
    if (!needed.length) {
      return { ...offer, inventory: null };
    }

    let remaining = Infinity;
    let anyStop = false;
    let stopSaleFlag = false;
    let allUnmanaged = true;
    let allKnown = true;
    for (const line of needed) {
      const snap = byCode.get(line.room_code);
      if (!snap) {
        allKnown = false;
        continue;
      }
      // RQ / unmanaged nights do not consume allotment — keep offers visible.
      if (snap.status === "unmanaged" || snap.status === "on_request" || snap.on_request) {
        continue;
      }
      allUnmanaged = false;
      const nightStop = Array.isArray(snap.nights)
        ? snap.nights.some((n) => Boolean(n?.stop_sale))
        : false;
      stopSaleFlag = stopSaleFlag || Boolean(snap.stop_sale) || nightStop;
      anyStop =
        anyStop ||
        stopSaleFlag ||
        snap.status === "sold_out" ||
        snap.bookable === false;
      const rem = Math.max(0, Number(snap.remaining) || 0);
      const combos = Math.floor(rem / Math.max(1, line.qty));
      remaining = Math.min(remaining, combos);
    }

    // Missing inventory rows / room codes → treat as open (contract default), not
    // "cannot verify". Only explicit stop_sale / sold_out / exhausted allotment hide.
    if (!allKnown && remaining === Infinity) {
      return {
        ...offer,
        inventory: {
          status: "unmanaged",
          remaining: null,
          label_he: "לא הוגדר מלאי",
          bookable: true,
          roomsNeeded: needed,
        },
      };
    }

    if (allUnmanaged || remaining === Infinity) {
      return {
        ...offer,
        inventory: {
          status: "unmanaged",
          remaining: null,
          label_he: "לא הוגדר מלאי",
          bookable: true,
          roomsNeeded: needed,
        },
      };
    }

    const rem = Number.isFinite(remaining) ? remaining : 0;
    let status = "available";
    if (rem <= 0 || anyStop) status = "sold_out";
    else if (rem <= 3) status = "limited";

    const label_he =
      status === "sold_out"
        ? "אזל"
        : status === "limited"
          ? `נותרו ${rem} חדרים`
          : "זמין";

    return {
      ...offer,
      inventory: {
        status,
        remaining: rem,
        label_he,
        bookable: status !== "sold_out" && rem > 0,
        stop_sale: stopSaleFlag,
        roomsNeeded: needed,
      },
    };
  });
}

export function offerInventoryKey(offer) {
  return offer?.id ?? offer?.room?.id ?? null;
}

/** Build Map(offerKey → inventory) from enriched offers. */
export function buildInventoryByOfferMap(enrichedOffers) {
  const map = new Map();
  for (const offer of enrichedOffers || []) {
    const key = offerInventoryKey(offer);
    if (key == null) continue;
    map.set(key, offer.inventory ?? null);
  }
  return map;
}

/**
 * Attach stay inventory to display offers and drop unbookable rows.
 * Fail-closed only while loading or when the availability request itself failed.
 * Missing map keys / missing room rows → unmanaged (shown) unless stop_sale/sold_out.
 */
export function applyInventoryGateToOffers(baseOffers, inventoryByOffer, { loading = false, failed = false } = {}) {
  if (loading || failed) return [];
  if (!inventoryByOffer) return [];

  const withInv = (baseOffers || []).map((offer) => {
    const key = offerInventoryKey(offer);
    if (key == null || !inventoryByOffer.has(key)) {
      return {
        ...offer,
        inventory: {
          status: "unmanaged",
          remaining: null,
          label_he: "לא הוגדר מלאי",
          bookable: true,
        },
      };
    }
    const inv = inventoryByOffer.get(key);
    if (!inv) {
      return {
        ...offer,
        inventory: {
          status: "unmanaged",
          remaining: null,
          label_he: "לא הוגדר מלאי",
          bookable: true,
        },
      };
    }
    return { ...offer, inventory: inv };
  });
  return filterBookableInventoryOffers(withInv);
}

/** Hide offers blocked by Stop Sale / sold-out. Missing/unmanaged inventory stays visible. */
export function filterBookableInventoryOffers(offers) {
  return (offers || []).filter((offer) => {
    const inv = offer?.inventory;
    // No snapshot → allow (contract default). Explicit blocks only.
    if (!inv) return true;
    if (inv.bookable === false) return false;
    if (inv.status === "sold_out") return false;
    if (inv.stop_sale) return false;
    return true;
  });
}

export async function createBelvedereInventoryHold({
  checkIn,
  checkOut,
  rooms,
  dynamicPackageId,
  actor,
  campaignCode = BELVEDERE_INVENTORY_CAMPAIGN_CODE,
} = {}) {
  const res = await axios.post(
    `${PUBLIC_BASE}/holds`,
    {
      campaign_code: campaignCode,
      check_in: checkIn,
      check_out: checkOut,
      rooms,
      dynamic_package_id: dynamicPackageId || null,
      actor: actor || undefined,
      ttl_minutes: BELVEDERE_HOLD_TTL_MINUTES,
    },
    { withCredentials: true },
  );
  return res.data;
}

export async function attachBelvedereInventoryHold(holdToken, dynamicPackageId) {
  const res = await axios.post(
    `${PUBLIC_BASE}/holds/${encodeURIComponent(holdToken)}/attach`,
    { dynamic_package_id: dynamicPackageId },
    { withCredentials: true },
  );
  return res.data;
}

export async function releaseBelvedereInventoryHold(holdToken, reason = "released") {
  if (!holdToken) return null;
  const res = await axios.post(
    `${PUBLIC_BASE}/holds/${encodeURIComponent(holdToken)}/release`,
    { reason },
    { withCredentials: true },
  );
  return res.data;
}

export function inventoryBadgeTone(status) {
  if (status === "sold_out") return "sold-out";
  if (status === "limited") return "limited";
  if (status === "available") return "available";
  return "unknown";
}
